import { useState, useCallback } from 'react';
import { documentService } from '../../services/documentService';

interface PreviewState {
    isOpen: boolean;
    loading: boolean;
    error: string | null;
    documentUrl: string | null;
    filename: string;
    currentDocId: string | null;
    blob: Blob | null;
    currentIndex: number;
    /** 1-indexed page the modal should open on. ``null`` means "page 1
     *  by default" (e.g. user clicked the preview button). Set by the
     *  page-preview tiles (#755) to deep-link to a specific page. */
    initialPage: number | null;
}

interface UsePreviewStateOptions {
    t: (key: string) => string;
    /** Ordered list of document IDs for prev/next navigation. When provided with ≥2 entries, navigation arrows are shown. */
    documentList?: string[];
}

export interface UsePreviewStateReturn {
    previewState: PreviewState;
    rotating: boolean;
    handlePreview: (
        docId: string,
        fallbackFilename?: string,
        initialPage?: number,
    ) => Promise<void>;
    handlePreviewClose: () => void;
    handlePreviewDownload: () => void;
    handlePreviewRotate: (degrees: 90 | 180 | 270) => Promise<void>;
    handleNavigate: (newIndex: number) => Promise<void>;
    canNavigatePrev: boolean;
    canNavigateNext: boolean;
}

const INITIAL_PREVIEW_STATE: PreviewState = {
    isOpen: false,
    loading: false,
    error: null,
    documentUrl: null,
    filename: '',
    currentDocId: null,
    blob: null,
    currentIndex: -1,
    initialPage: null,
};

export function usePreviewState({ t, documentList }: UsePreviewStateOptions): UsePreviewStateReturn {
    const [previewState, setPreviewState] = useState<PreviewState>(INITIAL_PREVIEW_STATE);
    const [rotating, setRotating] = useState(false);

    const _fetchAndOpen = useCallback(
        async (
            docId: string,
            index: number,
            fallbackFilename?: string,
            initialPage?: number,
        ) => {
            // Revoke previous blob URL before fetching new one (prevents memory leaks on navigation)
            setPreviewState((prev) => {
                if (prev.documentUrl) URL.revokeObjectURL(prev.documentUrl);
                return {
                    isOpen: true,
                    loading: true,
                    error: null,
                    documentUrl: null,
                    filename: fallbackFilename || prev.filename || 'document',
                    currentDocId: docId,
                    blob: null,
                    currentIndex: index,
                    initialPage: initialPage ?? null,
                };
            });

            try {
                const { blobUrl, filename, blob } = await documentService.getDocumentForPreview(docId);
                setPreviewState((prev) => ({
                    ...prev,
                    loading: false,
                    documentUrl: blobUrl,
                    filename: filename || fallbackFilename || 'document',
                    blob,
                }));
            } catch (err) {
                console.error('Error loading document for preview:', err);
                setPreviewState((prev) => ({
                    ...prev,
                    loading: false,
                    error: t('documents.previewError') || 'Failed to load document',
                }));
            }
        },
        [t]
    );

    const handlePreview = useCallback(
        async (docId: string, fallbackFilename?: string, initialPage?: number) => {
            const index = documentList ? documentList.indexOf(docId) : -1;
            await _fetchAndOpen(docId, index, fallbackFilename, initialPage);
        },
        [documentList, _fetchAndOpen]
    );

    const handleNavigate = useCallback(
        async (newIndex: number) => {
            if (!documentList || newIndex < 0 || newIndex >= documentList.length) return;
            await _fetchAndOpen(documentList[newIndex], newIndex);
        },
        [documentList, _fetchAndOpen]
    );

    const canNavigatePrev =
        !!documentList && documentList.length >= 2 && previewState.currentIndex > 0;
    const canNavigateNext =
        !!documentList &&
        documentList.length >= 2 &&
        previewState.currentIndex >= 0 &&
        previewState.currentIndex < documentList.length - 1;

    const handlePreviewClose = useCallback(() => {
        if (previewState.documentUrl) {
            URL.revokeObjectURL(previewState.documentUrl);
        }
        setPreviewState(INITIAL_PREVIEW_STATE);
    }, [previewState.documentUrl]);

    const handlePreviewDownload = useCallback(() => {
        if (!previewState.blob || !previewState.filename) return;

        const url = URL.createObjectURL(previewState.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = previewState.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [previewState.blob, previewState.filename]);

    const handlePreviewRotate = useCallback(
        async (degrees: 90 | 180 | 270) => {
            const docId = previewState.currentDocId;
            if (!docId || rotating) return;

            setRotating(true);
            try {
                await documentService.rotateDocument(docId, degrees);

                if (previewState.documentUrl) {
                    URL.revokeObjectURL(previewState.documentUrl);
                }

                const { blobUrl, filename, blob } = await documentService.getDocumentForPreview(docId);
                setPreviewState((prev) => ({
                    ...prev,
                    documentUrl: blobUrl,
                    filename: filename || prev.filename,
                    blob,
                }));
            } catch (err) {
                console.error('Error rotating document:', err);
            } finally {
                setRotating(false);
            }
        },
        [previewState.currentDocId, previewState.documentUrl, rotating]
    );

    return {
        previewState,
        rotating,
        handlePreview,
        handlePreviewClose,
        handlePreviewDownload,
        handlePreviewRotate,
        handleNavigate,
        canNavigatePrev,
        canNavigateNext,
    };
}
